import type { PropertyType } from './frontmatter-yaml';

export type { PropertyType } from './frontmatter-yaml';
export { PROPERTY_TYPES } from './frontmatter-yaml';

/** What the row renders for a value: one of the editors, or a read-only view. */
export type EditorKind =
  | 'text'
  | 'number'
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'chips'
  | 'readonly';

export type Properties = Record<string, unknown>;

function isPrimitive(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Primitives only: a list of scalars can be chips; nested structures cannot. */
export function isFlatList(
  value: unknown,
): value is (string | number | boolean)[] {
  return Array.isArray(value) && value.every(isPrimitive);
}

/**
 * Which editor a (key, type, value) triple gets. Declared type wins when the
 * value fits; otherwise fall back to the value's shape so nothing is coerced
 * behind the user's back.
 */
export function editorKindFor(
  type: PropertyType | undefined,
  value: unknown,
): EditorKind {
  if (isPlainObject(value)) {
    return 'readonly';
  }
  if (Array.isArray(value) && !isFlatList(value)) {
    return 'readonly';
  }
  switch (type) {
    case 'list':
    case 'tags':
      if (
        Array.isArray(value) ||
        typeof value === 'string' ||
        value === null ||
        value === undefined
      ) {
        return 'chips';
      }
      break;
    case 'checkbox':
      if (typeof value === 'boolean') {
        return 'checkbox';
      }
      break;
    case 'number':
      if (
        typeof value === 'number' ||
        value === null ||
        value === undefined
      ) {
        return 'number';
      }
      break;
    case 'date':
      if (
        typeof value === 'string' ||
        value === null ||
        value === undefined
      ) {
        return 'date';
      }
      break;
    case 'datetime':
      if (
        typeof value === 'string' ||
        value === null ||
        value === undefined
      ) {
        return 'datetime';
      }
      break;
    default:
      break;
  }
  if (Array.isArray(value)) {
    return 'chips';
  }
  if (typeof value === 'boolean') {
    return 'checkbox';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  return 'text';
}

export function chipsOf(value: unknown, type?: PropertyType): string[] {
  if (Array.isArray(value)) {
    return value.filter(isPrimitive).map((item) => String(item));
  }
  if (typeof value === 'string') {
    const parts =
      type === 'tags' ? value.split(/[\s,]+/) : value.split(',');
    return parts.map((part) => part.trim()).filter(Boolean);
  }
  return [];
}

const TAG_CHARS = /^[\p{L}\p{N}\p{M}_\-/]+$/u;

export function normalizeTagInput(raw: string): string | null {
  const tag = raw
    .trim()
    .replace(/^#+/, '')
    .trim()
    .replace(/\/{2,}/g, '/')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  if (!tag || !/\p{L}/u.test(tag) || !TAG_CHARS.test(tag)) {
    return null;
  }
  return tag;
}

export function normalizeChip(
  raw: string,
  type: PropertyType | undefined,
): string | null {
  if (type === 'tags') {
    return normalizeTagInput(raw);
  }
  const value = raw.trim();
  return value ? value : null;
}

export function addChips(
  current: string[],
  raw: string,
  type: PropertyType | undefined,
): string[] {
  const separator = type === 'tags' ? /[\s,]+/ : /,/;
  const next = [...current];
  for (const piece of raw.split(separator)) {
    const chip = normalizeChip(piece, type);
    if (chip && !next.includes(chip)) {
      next.push(chip);
    }
  }
  return next;
}

export function emptyValueFor(type: PropertyType): unknown {
  switch (type) {
    case 'list':
    case 'tags':
      return [];
    case 'checkbox':
      return false;
    case 'number':
    case 'date':
    case 'datetime':
      return null;
    default:
      return '';
  }
}

export function parseNumberInput(raw: string): number | null {
  const text = raw.trim();
  if (!text) {
    return null;
  }
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

export function dateInputValue(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  const match = /^(\d{4}-\d{2}-\d{2})(?:$|[T ])/.exec(value.trim());
  return match?.[1] ?? '';
}

export function datetimeInputValue(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(value.trim());
  return match ? `${match[1]}T${match[2]}` : '';
}

export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return (
      a.length === b.length && a.every((item, index) => sameValue(item, b[index]))
    );
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    return (
      keys.length === Object.keys(b).length &&
      keys.every((key) => key in b && sameValue(a[key], b[key]))
    );
  }
  return false;
}

export function effectiveType(
  key: string,
  value: unknown,
  types: Record<string, PropertyType>,
): PropertyType {
  const known = types[key];
  if (known) {
    return known;
  }
  if (Array.isArray(value)) {
    return key === 'tags' ? 'tags' : 'list';
  }
  if (typeof value === 'boolean') {
    return 'checkbox';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  return 'text';
}

export function normalizeKeyInput(raw: string): string | null {
  const key = raw.trim();
  if (!key || key.length > 100 || /[\r\n]/.test(key)) {
    return null;
  }
  return key;
}

export function formatTimestamp(ms: number | undefined): string {
  if (ms === undefined || !Number.isFinite(ms) || ms <= 0) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString();
  }
}
